import { useState, useEffect, useRef } from "react";
import { ChatCircle, X, PaperPlaneTilt, Robot, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

const URL_RE = /(https?:\/\/[^\s]+)/g;

function linkify(text, sender) {
  if (!text) return text;
  const parts = text.split(URL_RE);
  return parts.map((part, idx) => {
    if (URL_RE.test(part)) {
      const isCheckout = part.includes("checkout.stripe.com");
      if (isCheckout && sender === "bot") {
        return (
          <a
            key={idx}
            href={part}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-white px-3 py-2 text-xs font-semibold tracking-wider uppercase"
            data-testid="bot-pay-link"
          >
            <CreditCard size={14} weight="duotone" /> Pay now
          </a>
        );
      }
      return (
        <a key={idx} href={part} target="_blank" rel="noreferrer" className="underline text-brand-light break-all">
          {part}
        </a>
      );
    }
    return <span key={idx}>{part}</span>;
  });
}

export default function BotChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/bot/me/messages");
      setMessages(data);
    } catch (_e) {}
  };

  useEffect(() => { if (open) load(); }, [open]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    const msg = text.trim();
    setText("");
    setMessages((prev) => [...prev, { id: `temp-${Date.now()}`, sender: "client", text: msg, created_at: new Date().toISOString() }]);
    setBusy(true);
    try {
      const { data } = await api.post("/bot/message", { message: msg });
      setMessages((prev) => [...prev, { id: `bot-${Date.now()}`, sender: "bot", text: data.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-44 z-40 bg-brand hover:bg-brand-light text-white p-4 shadow-xl flex items-center gap-2"
        data-testid="bot-chat-toggle"
      >
        <ChatCircle size={20} weight="fill" />
        <span className="tracker text-white">Need to reschedule?</span>
      </button>

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-full max-w-md card-flat flex flex-col" style={{ height: "min(560px, 80vh)" }} data-testid="bot-chat-window">
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-brand/15 border border-brand/40 flex items-center justify-center">
                <Robot size={18} weight="duotone" className="text-brand" />
              </div>
              <div>
                <div className="font-display text-lg leading-none">Traina Bot</div>
                <div className="tracker text-zinc-500 mt-1">Reschedule · Cancel · Ask</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" data-testid="bot-close">
              <X size={20} weight="bold" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="bot-messages">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Robot size={32} weight="duotone" className="text-zinc-700 mx-auto mb-3" />
                <p className="text-sm text-zinc-400">Hi! Tell me which session you want to move and roughly when works for you. Your coach approves every change.</p>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2 text-sm whitespace-pre-wrap break-words ${m.sender === "client" ? "bg-brand text-white" : "bg-[#1A1A1A] border border-white/10 text-zinc-200"}`}>
                  {linkify(m.text, m.sender)}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A1A] border border-white/10 px-4 py-2 text-sm text-zinc-500">
                  <span className="inline-block w-2 h-2 bg-zinc-500 mr-1 animate-pulse" />
                  <span className="inline-block w-2 h-2 bg-zinc-500 mr-1 animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="inline-block w-2 h-2 bg-zinc-500 animate-pulse" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="px-5 py-3 border-t border-white/10 flex items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your message…"
              className="flex-1 px-3 py-2 border"
              disabled={busy}
              data-testid="bot-input"
            />
            <button type="submit" disabled={busy || !text.trim()} className="btn-primary !py-2 !px-3" data-testid="bot-send">
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
